import {
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { IBillAuditLogResponse, IBillScanAuditLogsQueryParams } from './types';
import { BILL_LIST_QUERY_KEY } from './keys';
import {
  fetchBillScanAuditLogs,
  fetchBillsList,
  fetchBillsSummary,
  fetchBillsUserPersist,
} from './services';
import { SCAN_AUDIT_LOG_PER_PAGE } from 'pages/Bills/List/constants/auditLog';
import {
  IBillsListParams,
  IBillsListResponse,
  IBillsUserPersistResponse,
} from '../types';
import { AxiosError } from 'axios';
import {
  IBillsSummaryRequestParams,
  IBillsSummaryResponse,
} from '../types/billSettings';

const billScanAuditLogsQueryKey = [
  BILL_LIST_QUERY_KEY.SCAN_BILL_AUDIT_LOGS,
  { per_page: SCAN_AUDIT_LOG_PER_PAGE },
] as const satisfies readonly [string, IBillScanAuditLogsQueryParams];

export const useInfiniteBillScanAuditLogs = ({
  enabled,
}: {
  enabled: boolean;
}) =>
  useInfiniteQuery({
    queryKey: billScanAuditLogsQueryKey,
    queryFn: fetchBillScanAuditLogs,
    initialPageParam: 1,
    getNextPageParam: (lastPage: IBillAuditLogResponse) => {
      const { pagination } = lastPage;
      return pagination?.links?.next ? pagination.current_page + 1 : undefined;
    },
    refetchOnWindowFocus: false,
    enabled,
    retry: false,
  });

export const useGetBillsSummary = (
  payload: IBillsSummaryRequestParams,
  options?: Omit<
    UseQueryOptions<IBillsSummaryResponse, AxiosError>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<IBillsSummaryResponse, AxiosError>({
    queryKey: [BILL_LIST_QUERY_KEY.BILLS_SUMMARY, payload],
    queryFn: () => fetchBillsSummary(payload),
    ...options,
  });

export const useGetBillsList = (
  payload: Partial<IBillsListParams>,
  options?: Omit<
    UseQueryOptions<IBillsListResponse, AxiosError>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<IBillsListResponse, AxiosError>({
    queryKey: [BILL_LIST_QUERY_KEY.BILLS_LIST, payload],
    queryFn: ({ signal }) => fetchBillsList(payload, signal),
    ...options,
  });

export const useGetBillsUserPersist = (
  options?: UseQueryOptions<IBillsUserPersistResponse, AxiosError>,
) =>
  useQuery<IBillsUserPersistResponse, AxiosError>({
    queryKey: [BILL_LIST_QUERY_KEY.BILLS_USER_PERSIST],
    queryFn: ({ signal }) => fetchBillsUserPersist(signal),
    staleTime: Infinity,
    ...options,
  });
